import { Helmet } from 'react-helmet-async';
import { H1, H2, H3, Body } from '../components/ui/Typography';

export default function Disclaimers() {
  return (
    <>
      <Helmet>
        <title>RentFi | Disclaimers</title>
        <meta 
          name="description" 
          content="Important disclaimers regarding RentFi's real estate investment platform, including risk disclosures, limitations of liability, and regulatory compliance information." 
        />
        <meta 
          name="keywords" 
          content="RentFi disclaimers, risk disclosures, legal notices, investment disclaimers, regulatory compliance" 
        />
        <link rel="canonical" href="https://rentfi.io/disclaimers" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <H1 className="text-primary mb-4">Disclaimers</H1>
            <p className="text-gray-500 mb-8">Last Updated: January 30, 2025</p>

            {/* General Disclaimers */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">1. General Disclaimers</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">1.1 No Financial Advice</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Content on our platform is for informational purposes only</li>
                    <li>We do not provide investment, legal, or tax advice</li>
                    <li>Users should consult professional advisors for specific guidance</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">1.2 Investment Risks</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Cryptocurrency investments carry significant risks</li>
                    <li>Past performance does not guarantee future results</li>
                    <li>Users may lose their entire investment</li>
                    <li>Market volatility can affect token value</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">1.3 Token Classification</H3>
                  <Body className="text-gray-600 mb-2">RENT tokens:</Body>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Are not securities or financial instruments</li>
                    <li>Do not represent ownership of real estate</li>
                    <li>Do not constitute collective investments</li>
                    <li>Are not regulated by financial authorities</li>
                    <li>Can only be traded on decentralized exchanges</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Platform Disclaimers */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">2. Platform Disclaimers</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">2.1 Technical Risks</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Smart contract vulnerabilities may exist</li>
                    <li>Blockchain networks may experience disruptions</li>
                    <li>Technical issues may affect distributions</li>
                    <li>Digital wallet security is user responsibility</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">2.2 Performance</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>We do not guarantee platform availability</li>
                    <li>Service interruptions may occur</li>
                    <li>Distribution timing may vary</li>
                    <li>Technical upgrades may affect functionality</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">2.3 Third-Party Services</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>We are not responsible for third-party services</li>
                    <li>External links are provided for convenience only</li>
                    <li>Users access third-party services at their own risk</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Legal Compliance */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">3. Legal Compliance</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">3.1 Regulatory Status</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Platform operates under applicable jurisdiction</li>
                    <li>Users must comply with local regulations</li>
                    <li>Token trading may be restricted in certain jurisdictions</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">3.2 Anti-Money Laundering</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>We comply with applicable AML regulations</li>
                    <li>Users must not use platform for illegal activities</li>
                    <li>We cooperate with law enforcement investigations</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Property Investment Disclaimers */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">4. Property Investment Disclaimers</H2>
              
              <div className="space-y-6">
                <div>
                  <H3 className="text-gray-900 mb-2">4.1 Real Estate Risks</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Property values may fluctuate</li>
                    <li>Rental income is not guaranteed</li>
                    <li>Market conditions affect returns</li>
                    <li>Property management risks exist</li>
                  </ul>
                </div>

                <div>
                  <H3 className="text-gray-900 mb-2">4.2 Distribution Mechanics</H3>
                  <ul className="list-disc pl-6 space-y-2 text-gray-600">
                    <li>Distribution amounts may vary</li>
                    <li>Minimum token requirements apply</li>
                    <li>Snapshot mechanisms determine eligibility</li>
                    <li>Smart contract execution is automated</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <H2 className="text-primary mb-4">5. Contact Information</H2>
              <Body className="text-gray-600">
                For questions about our Disclaimers:<br />
                <a 
                  href="mailto:support@rentfi.helpcrunch-mail.com" 
                  className="text-secondary hover:text-secondary-dark"
                  aria-label="Email RentFi support team with questions about disclaimers"
                >
                  support@rentfi.helpcrunch-mail.com
                </a>
              </Body>
            </div>

            {/* Updates and Modifications */}
            <div>
              <H2 className="text-primary mb-4">6. Updates and Modifications</H2>
              <Body className="text-gray-600">
                We reserve the right to modify these disclaimers at any time without notice.
              </Body>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}